import { saveAs } from "file-saver";
import { isAxiosError } from "axios";
import Base from "components/Base";
import { useToastContext } from "components/alert/ToastContext";
import AuthContext from "contexts/AuthContext";
import { EmitirCartaCorrecaoNotaFiscalSaida, ItemCartaCorrecaoNotaFiscalSaida, ResultadoCartaCorrecaoNotaFiscalSaida } from "model/vendas/CartaCorrecaoNotaFiscalSaida";
import NotaFiscalSaida from "model/vendas/NotaFiscalSaida";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NotaFiscalSaidaService from "service/vendas/NotaFiscalSaidaService";

const service = new NotaFiscalSaidaService();
const estadosPendentes = ["PENDENTE", "INCERTA", "MANUAL", "EXECUTANDO"];
interface ItemFormulario { idItem: number; numeroItem: number; descricaoProduto: string; codigoProdutoFiscal: string; ean: string; }

function uuid(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function NotaFiscalSaidaCartaCorrecao() {
    const { id } = useParams();
    const idNota = Number(id);
    const navigate = useNavigate();
    const { acoes } = useContext(AuthContext);
    const { msgErro, msgSucesso } = useToastContext();
    const permitido = acoes.some(acao => acao.sigla === "NOTA_FISCAL_SAIDA_CORRIGIR");
    const [nota, setNota] = useState<NotaFiscalSaida | null>(null);
    const [historico, setHistorico] = useState<ResultadoCartaCorrecaoNotaFiscalSaida[]>([]);
    const [itens, setItens] = useState<ItemFormulario[]>([]);
    const [informacoes, setInformacoes] = useState("");
    const [correcao, setCorrecao] = useState("");
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [confirmando, setConfirmando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const envioEmCurso = useRef(false);

    function preencher(novaNota: NotaFiscalSaida) {
        setNota(novaNota);
        setInformacoes(novaNota.informacoesComplementares ?? "");
        setItens((novaNota.itens ?? []).map(item => ({
            idItem: item.id, numeroItem: item.numeroItem,
            descricaoProduto: item.descricaoProduto, codigoProdutoFiscal: item.codigoProdutoFiscal, ean: item.ean ?? ""
        })));
        setCorrecao("");
    }

    useEffect(() => {
        let ativo = true;
        setNota(null);
        setHistorico([]);
        setErro(null);
        setCarregando(true);
        if (!permitido || !Number.isSafeInteger(idNota) || idNota <= 0) {
            setCarregando(false);
            return;
        }
        Promise.all([service.consultar(idNota), service.listarCartasCorrecao(idNota)])
            .then(([novaNota, cartas]) => {
                if (!ativo) return;
                preencher(novaNota);
                setHistorico(cartas);
            })
            .catch(() => { if (ativo) setErro("Não foi possível carregar a nota e o histórico de cartas de correção."); })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
        // O carregamento depende da nota e da autorização; o formulário é preenchido uma vez.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNota, permitido]);

    const autorizada = nota?.modelo === "NFE" && ["AUTORIZADA", "AUTORIZADA_ESTOQUE_PENDENTE"].includes(nota.status);
    const historicoPendente = historico.some(carta => estadosPendentes.includes(carta.status));
    const bloqueado = carregando || enviando || !autorizada || historicoPendente;


    const alteracoes: ItemCartaCorrecaoNotaFiscalSaida[] = [];
    let camposInvalidos = false;
    const linhas: string[] = correcao.trim() ? [correcao.trim()] : [];
    const informacoesAlteradas = nota !== null && informacoes.trim() !== (nota.informacoesComplementares ?? "").trim();
    if (informacoesAlteradas) linhas.push(`Informações complementares: considerar ${informacoes.trim() || "sem informações complementares."}`);
    for (const item of itens) {
        const original = nota?.itens?.find(i => i.id === item.idItem);
        if (!original) continue;
        const alteracao: ItemCartaCorrecaoNotaFiscalSaida = { idItem: item.idItem };
        if (item.descricaoProduto.trim() !== original.descricaoProduto.trim()) {
            alteracao.descricaoProduto = item.descricaoProduto.trim();
            linhas.push(`Item ${item.numeroItem} - descrição: ${alteracao.descricaoProduto}`);
        }
        if (item.codigoProdutoFiscal.trim() !== original.codigoProdutoFiscal.trim()) {
            alteracao.codigoProdutoFiscal = item.codigoProdutoFiscal.trim();
            linhas.push(`Item ${item.numeroItem} - código do produto: ${alteracao.codigoProdutoFiscal}`);
        }
        if (item.ean.trim() !== (original.ean ?? "").trim()) {
            alteracao.ean = item.ean.trim();
            linhas.push(`Item ${item.numeroItem} - EAN: ${alteracao.ean || "SEM GTIN"}`);
        }
        if (!item.descricaoProduto.trim() || !item.codigoProdutoFiscal.trim()
            || !/^(?:|SEM GTIN|[0-9]{8}|[0-9]{12,14})$/.test(item.ean.trim())) camposInvalidos = true;
        if (Object.keys(alteracao).length > 1) alteracoes.push(alteracao);
    }
    const anterior = [...historico].reverse().find(carta => carta.status === "REGISTRADO")?.correcao;
    const textoAtual = linhas.join("\n").trim();
    const textoConsolidado = anterior && textoAtual ? `${anterior}\n${textoAtual}` : textoAtual;
    const valido = textoAtual.length > 0 && textoConsolidado.length >= 15 && textoConsolidado.length <= 1000 && !camposInvalidos;

    function alterarItem(idItem: number, campo: "descricaoProduto" | "codigoProdutoFiscal" | "ean", valor: string) {
        setItens(anteriores => anteriores.map(item => item.idItem === idItem ? { ...item, [campo]: valor } : item));
    }

    async function enviar() {
        if (!nota || !permitido || envioEmCurso.current || bloqueado || !valido) return;
        envioEmCurso.current = true;
        setEnviando(true);
        setErro(null);
        let pedido: EmitirCartaCorrecaoNotaFiscalSaida;
        try {
            pedido = {
                idRequisicao: uuid(), correcao: correcao.trim() || undefined,
                informacoesComplementares: informacoesAlteradas ? informacoes.trim() : undefined,
                itens: alteracoes.length ? alteracoes : undefined
            };
        } catch {
            setErro("Não foi possível preparar o envio da carta. Tente novamente.");
            setEnviando(false);
            envioEmCurso.current = false;
            return;
        }
        try {
            await service.emitirCartaCorrecao(nota.id, pedido);
            setConfirmando(false);
            msgSucesso("Carta de correção", "Carta agendada na SEFAZ. Acompanhe a confirmação no histórico.");
            try {
                const [novaNota, cartas] = await Promise.all([service.consultar(nota.id), service.listarCartasCorrecao(nota.id)]);
                preencher(novaNota);
                setHistorico(cartas);
            } catch {
                // Evita outra emissão com campos e histórico desatualizados.
                setNota(null);
                setErro("A carta foi registrada, mas a consulta não pôde ser atualizada. Reabra esta tela para continuar.");
            }
        } catch (error) {
            let mensagem = "Não foi possível confirmar o envio. Verifique o histórico antes de tentar novamente.";
            if (isAxiosError<{ detail?: string; message?: string }>(error)) {
                mensagem = error.response?.data?.detail || error.response?.data?.message || mensagem;
            }
            setErro(mensagem);
            setConfirmando(false);
            msgErro("Carta de correção", mensagem);
            try { setHistorico(await service.listarCartasCorrecao(nota.id)); } catch { /* Mantém o histórico atual até que a consulta possa ser concluída. */ }
        } finally {
            setEnviando(false);
            envioEmCurso.current = false;
        }
    }

    useEffect(()=>{if(!historicoPendente)return;let ativo=true;const timer=window.setInterval(()=>{service.listarCartasCorrecao(idNota).then(cartas=>{if(ativo)setHistorico(cartas);}).catch(()=>{});},5000);return()=>{ativo=false;window.clearInterval(timer);};},[historicoPendente,idNota]);
    return <Base tituloPagina="Carta de correção de nota fiscal">
        <div className="card" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <h2>Carta de correção {nota?.numeroNota != null ? `— NF-e ${nota.numeroNota}` : ""}</h2>
                <Button label="Voltar à consulta" icon="pi pi-arrow-left" outlined disabled={enviando}
                    onClick={() => navigate(`/notaFiscalSaida/consultar/${idNota}`)} />
            </div>
            {!permitido && <p role="alert">Sem permissão para emitir cartas de correção.</p>}
            {carregando && <p role="status">Carregando nota e histórico...</p>}
            {erro && <p className="text-danger" role="alert">{erro}</p>}
            {nota && permitido && <>
                <p><strong>Série:</strong> {nota.numeroSerie ?? "—"} · <strong>Destinatário:</strong> {nota.nomeDestinatario || "—"} · <strong>Situação:</strong> {nota.status.replaceAll("_", " ")}</p>
                <p>Corrija informações complementares, descrição, código fiscal do produto ou EAN.
                    A correção deve manter a mercadoria e a tributação. Quantidades, valores, impostos,
                    datas e identificação do emitente ou destinatário não podem ser alterados nesta tela.</p>
                {!autorizada && <p role="status">Somente NF-e modelo 55 autorizada pode receber uma carta de correção.</p>}
                {historicoPendente && <p role="status">Existe uma carta pendente. Confira seu registro na SEFAZ antes de emitir outra.</p>}
                <fieldset disabled={bloqueado} style={{ border: 0, padding: 0 }}>
                    <label htmlFor="informacoesCarta"><strong>Informações complementares da nota</strong></label>
                    <InputTextarea id="informacoesCarta" value={informacoes} rows={3} maxLength={5000} className="w-100 mt-2 mb-3"
                        onChange={e => setInformacoes(e.target.value)} />
                    <h3>Itens</h3>
                    {itens.map(item => <div key={item.idItem} className="mb-3" style={{ border: "1px solid #ddd", padding: 12, borderRadius: 6 }}>
                        <strong>Item {item.numeroItem}</strong>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 8 }}>
                            <div>
                                <label htmlFor={`descricao-${item.idItem}`}>Descrição do produto</label>
                                <InputText id={`descricao-${item.idItem}`} className="w-100" maxLength={120} value={item.descricaoProduto}
                                    onChange={e => alterarItem(item.idItem, "descricaoProduto", e.target.value)} />
                            </div>
                            <div>
                                <label htmlFor={`codigo-${item.idItem}`}>Código fiscal do produto</label>
                                <InputText id={`codigo-${item.idItem}`} className="w-100" maxLength={60} value={item.codigoProdutoFiscal}
                                    onChange={e => alterarItem(item.idItem, "codigoProdutoFiscal", e.target.value)} />
                            </div>
                            <div>
                                <label htmlFor={`ean-${item.idItem}`}>EAN / GTIN</label>
                                <InputText id={`ean-${item.idItem}`} className="w-100" maxLength={14} value={item.ean}
                                    onChange={e => alterarItem(item.idItem, "ean", e.target.value)} />
                                <small>8, 12, 13 ou 14 dígitos; vazio ou SEM GTIN quando não houver.</small>
                            </div>
                        </div>
                    </div>)}
                    {camposInvalidos && <p className="text-danger">Preencha a descrição e o código dos itens e confira o EAN.</p>}
                    <label htmlFor="textoCarta"><strong>Texto adicional da correção (opcional)</strong></label>
                    <InputTextarea id="textoCarta" className="w-100 mt-2" rows={3} maxLength={1000} value={correcao}
                        onChange={e => setCorrecao(e.target.value)} />
                    <p>As alterações dos campos e as cartas anteriores serão incluídas no texto enviado.</p>
                    <h3>Texto consolidado da carta</h3>
                    <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", background: "#f5f5f5", padding: 12 }}>{textoConsolidado || "Informe uma correção para visualizar o texto."}</pre>
                    <p className={textoConsolidado.length > 1000 ? "text-danger" : ""}>{textoConsolidado.length}/1000 caracteres. Mínimo: 15.</p>
                </fieldset>
                <Button label="Revisar e emitir carta" icon="pi pi-send" loading={enviando} disabled={bloqueado || !valido}
                    onClick={() => setConfirmando(true)} />
                <h3 className="mt-4">Histórico de cartas de correção</h3>
                <DataTable value={historico} dataKey="idEvento" emptyMessage="Nenhuma carta emitida." responsiveLayout="scroll">
                    <Column field="idEvento" header="Evento" />
                    <Column field="status" header="Situação" />
                    <Column field="correcao" header="Texto da correção" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} />
                </DataTable>
            </>}
        </div>
        <Dialog header="Confirmar carta de correção" visible={confirmando} modal style={{ width: "min(700px, 95vw)" }}
            closable={!enviando} closeOnEscape={!enviando} onHide={() => { if (!enviando) setConfirmando(false); }}
            footer={<>
                <Button label="Voltar" outlined disabled={enviando} onClick={() => setConfirmando(false)} />
                <Button label="Emitir carta de correção" icon="pi pi-send" loading={enviando} disabled={!valido || (bloqueado && !enviando)}
                    onClick={() => { void enviar(); }} />
            </>}>
            <p>Confira o texto. Após o registro da carta, os campos corrigidos serão atualizados na nota.</p>
            <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{textoConsolidado}</pre>
        </Dialog>
    </Base>;
}
